import { useCallback, useState } from "react";

import { computeStepValue, getMaxStepValue } from "@/utils/stepValue";

interface UseStepValueOptions {
  totalRows: number;
}

interface UseStepValueReturn {
  stepValue: number;
  maxStepValue: number;
  setStepValue: (value: number) => void;
  resetStepValue: () => void;
}

/**
 * Hook that manages the step value based on totalRows.
 * When totalRows changes, stepValue always resets to follow totalRows.
 * User can override stepValue manually, but it resets whenever totalRows changes.
 */
export const useStepValue = ({
  totalRows
}: UseStepValueOptions): UseStepValueReturn => {
  const maxStepValue = getMaxStepValue(totalRows);

  const [prevTotalRows, setPrevTotalRows] = useState(totalRows);
  const [override, setOverride] = useState<number | null>(null);

  // Detect any totalRows change (including returning to the same value)
  // and clear user override so stepValue resets to follow totalRows
  if (prevTotalRows !== totalRows) {
    setPrevTotalRows(totalRows);
    setOverride(null);
  }

  // stepValue is always derived during render, never stale
  const stepValue = override ?? computeStepValue(totalRows);

  const setStepValue = useCallback((value: number) => {
    setOverride(value);
  }, []);

  // Reset (e.g. Custom Ordinal Reset) drops the override so the step follows the
  // data again: <2 rows → MIN_STEP (2), <10 → the row count, ≥10 → MAX_STEP (10).
  const resetStepValue = useCallback(() => {
    setOverride(null);
  }, []);

  return {
    stepValue,
    maxStepValue,
    setStepValue,
    resetStepValue
  };
};
